import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { cueCardsApi, type CueSet, type CueWord } from '@/api/cueCards';
import type { WordForms } from '@/api/vocab';
import { curatorApi } from '@/api/users';
import { humanizeError } from '@/lib/errors';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { usersApi } from '@/api/users';
import { cn } from '@/lib/utils';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { Modal } from '@/components/ui/Modal';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useBackHandler } from '@/lib/backButton';
import { ActionSheet } from '@/components/mobile/ActionSheet';
import { MAdd, MBlock, MEmpty, MRow, MTitle } from '@/components/mobile/MobileKit';

const LEVELS = ['', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

// Части речи и формы слова подписываются ключами переводов: те же подписи
// показываются ученику в тренажёре карточек (см. cueTrainer.pos*).
const POS_KEY: Record<string, string> = {
  noun: 'cueTrainer.posNoun', verb: 'cueTrainer.posVerb', adjective: 'cueTrainer.posAdjective',
  adverb: 'cueTrainer.posAdverb', phrase: 'cueTrainer.posPhrase', pronoun: 'cueTrainer.posPronoun',
  preposition: 'cueTrainer.posPreposition', conjunction: 'cueTrainer.posConjunction',
};
const posKey = (pos?: string) => (pos ? POS_KEY[pos.toLowerCase()] : '');

// Формы слова → ключи подписей (базовая форма — само слово, её не показываем).
const FORM_KEYS: Array<[keyof WordForms, string]> = [
  ['past', 'cueSets.formPast'], ['past_participle', 'cueSets.formPastPart'], ['present_participle', 'cueSets.formPresPart'],
  ['third_person', 'cueSets.formThird'], ['plural', 'cueSets.formPlural'], ['comparative', 'cueSets.formComp'], ['superlative', 'cueSets.formSuper'],
];
function formChips(forms?: WordForms): Array<{ labelKey: string; value: string }> {
  if (!forms) return [];
  return FORM_KEYS.flatMap(([key, labelKey]) => {
    const value = forms[key]?.trim();
    return value ? [{ labelKey, value }] : [];
  });
}

export function CuratorCueCardsPage() {
  const { t } = useTranslation();
  const ask = useConfirm();
  const qc = useQueryClient();
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ title: '', description: '', level: '', marathon: '', group: '' });
  const [openSet, setOpenSet] = useState<string | null>(null);

  const sets = useQuery({ queryKey: ['curator', 'cue-sets'], queryFn: () => cueCardsApi.listSets() });
  const marathons = useQuery({ queryKey: ['curator', 'marathons'], queryFn: () => curatorApi.listMyMarathons() });
  // ⚠️ Группы нужны, потому что заказчик просил адресовать набор не только всему
  // потоку: «хотелось бы чтобы можно было выбирать каким группам назначать».
  const groups = useQuery({ queryKey: ['curator', 'groups'], queryFn: usersApi.myCuratorGroups });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['curator', 'cue-sets'] });

  const marathonOptions = [
    { value: '', label: t('cueSets.noMarathonDraft') },
    ...(marathons.data?.items ?? []).map((m) => ({ value: m.marathon_id, label: m.title })),
  ];
  const groupOptions = [
    { value: '', label: t('cueSets.wholeCohort') },
    ...(groups.data?.items ?? []).map((g) => ({ value: g.id, label: g.name })),
  ];

  const createM = useMutation({
    mutationFn: () => cueCardsApi.createSet({
      title: form.title.trim(),
      description: form.description.trim() || undefined,
      level: form.level || undefined,
      marathon_id: form.marathon || null,
      // Группа выбрана — набор уходит только ей; не выбрана — всему потоку.
      audience_type: form.group ? 'group' : 'marathon',
      group_id: form.group || null,
    }),
    onSuccess: () => {
      setErr('');
      setFormOpen(false);
      setForm({ title: '', description: '', level: '', marathon: '', group: '' });
      invalidate();
    },
    onError: (e) => setErr(humanizeError(e)),
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => cueCardsApi.deleteSet(id),
    onSuccess: () => { setErr(''); invalidate(); }, onError: (e) => setErr(humanizeError(e)),
  });

  const items = sets.data?.items ?? [];
  const isMobile = useIsMobile();
  const [formOpen, setFormOpen] = useState(false);
  const [sheetFor, setSheetFor] = useState<CueSet | null>(null);
  const marathonTitleOf = (s: CueSet) => (marathons.data?.items ?? []).find((m) => m.marathon_id === s.marathon_id)?.title;
  const askDelete = async (s: CueSet) => {
    if (await ask({ message: t('cueSets.deleteConfirm', { title: s.title }), danger: true })) deleteM.mutate(s.id);
  };
  // На телефоне набор — отдельный экран (§4.15): «назад» Android и свайп iPhone ведут к списку наборов.
  const backToList = useCallback(() => {
    setOpenSet(null);
    return true;
  }, []);
  useBackHandler(backToList, isMobile && !!openSet);
  const createButton = (
    <Button loading={createM.isPending} disabled={!form.title.trim()} onClick={() => createM.mutate()}>
      <span className="material-symbols-outlined">add</span> {t('cueSets.create')}
    </Button>
  );

  // Поля «Нового набора» — одни на обе раскладки (на телефоне — окном во весь экран по «+», правило 3).
  const fields = (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Input label={t('cueSets.name')} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t('cueSets.setNameExample')} />
          </div>
          <div className="sm:col-span-2">
            <Input label={t('cueSets.descOptional')} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <Select label={t('cueSets.level')} value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}
            options={LEVELS.map((l) => ({ value: l, label: l || t('cueSets.anyLevel') }))} />
          <Select label={t('cueSets.marathon')} value={form.marathon} onChange={(e) => setForm({ ...form, marathon: e.target.value })} options={marathonOptions} />
          <Select label={t('cueSets.group')} value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} options={groupOptions} />
        </div>
  );

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §4.15): список наборов и экран набора ──────────────────────────
  if (isMobile) {
    const open = openSet ? items.find((s) => s.id === openSet) : undefined;
    if (open) {
      return (
        <SetCard
          phone
          set={open}
          marathonTitle={marathonTitleOf(open)}
          open
          onToggle={() => setOpenSet(null)}
          onDelete={() => askDelete(open)}
          onChanged={invalidate}
        />
      );
    }
    return (
      <div className="space-y-3">
        <MTitle
          title={t('cueSets.title')}
          subtitle={t('cueSets.subtitle')}
          right={<MAdd label={t('cueSets.newSet')} onClick={() => setFormOpen(true)} />}
        />
        {err && !formOpen && <p className="px-0.5 text-[13px] text-error">{err}</p>}
        {sets.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <MEmpty icon="style" title={t('cueSets.empty')} />
        ) : (
          <MBlock>
            {items.map((s, i) => (
              <MRow
                key={s.id}
                wrap
                onClick={() => setOpenSet(s.id)}
                title={s.title}
                meta={[t('cueSets.wordCount', { n: s.word_count }), s.level, marathonTitleOf(s) ?? t('cueSets.noMarathon')]
                  .filter(Boolean)
                  .join(' · ')}
                aside={
                  <button
                    type="button"
                    onClick={() => setSheetFor(s)}
                    aria-label={t('nav.more')}
                    className="grid h-11 w-11 flex-none place-items-center rounded-full text-on-surface-variant"
                  >
                    <span className="material-symbols-outlined text-[20px]">more_horiz</span>
                  </button>
                }
                last={i === items.length - 1}
              />
            ))}
          </MBlock>
        )}
        <ActionSheet
          open={!!sheetFor}
          onClose={() => setSheetFor(null)}
          title={sheetFor?.title}
          actions={
            sheetFor
              ? [
                  { label: t('cueSets.words'), icon: 'edit', onSelect: () => setOpenSet(sheetFor.id) },
                  { label: t('cueSets.deleteSet'), icon: 'delete', danger: true, onSelect: () => askDelete(sheetFor) },
                ]
              : []
          }
        />
        <Modal
          open={formOpen}
          onClose={() => setFormOpen(false)}
          title={t('cueSets.newSet')}
          phoneFull
          footer={<div className="grid">{createButton}</div>}
        >
          {err && <p className="mb-2 text-[13px] text-error">{err}</p>}
          {fields}
        </Modal>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <PageHeader title={t('cueSets.title')} subtitle={t('cueSets.subtitle')} />
      {err && <Card variant="subtle" className="text-error">{err}</Card>}

      {/* create set */}
      <Card>
        <CardTitle>{t('cueSets.newSet')}</CardTitle>
        {fields}
        <div className="mt-3">{createButton}</div>
      </Card>

      {/* sets */}
      {sets.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : items.length === 0 ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">{t('cueSets.empty')}</Card>
      ) : (
        items.map((s) => (
          <SetCard
            key={s.id}
            set={s}
            marathonTitle={marathonTitleOf(s)}
            open={openSet === s.id}
            onToggle={() => setOpenSet(openSet === s.id ? null : s.id)}
            onDelete={() => askDelete(s)}
            onChanged={invalidate}
          />
        ))
      )}
    </div>
  );
}

function SetCard({
  set, marathonTitle, open, onToggle, onDelete, onChanged, phone = false,
}: {
  set: CueSet; marathonTitle?: string; open: boolean; onToggle: () => void; onDelete: () => void; onChanged: () => void;
  /** Телефон: набор — отдельный экран со своей шапкой «назад», у слов — «⋯» вместо значков 28 px (§4.15). */
  phone?: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [wordSheet, setWordSheet] = useState<CueWord | null>(null);
  const [w, setW] = useState({ word: '', translation: '', example: '' });
  // Список слов одной строкой — основной способ ввода.
  const [bulk, setBulk] = useState('');
  const [bulkNote, setBulkNote] = useState('');
  const words = useQuery({
    queryKey: ['curator', 'cue-set', set.id, 'words'],
    queryFn: () => cueCardsApi.listWords(set.id),
    enabled: open,
    // While a freshly-added word's AI senses are still generating (empty), poll so
    // the meanings pop in on their own without the curator hitting refresh.
    refetchInterval: (q) => {
      const items = (q.state.data as { items: CueWord[] } | undefined)?.items ?? [];
      return items.some((it) => !(it.senses && it.senses.length)) ? 4000 : false;
    },
  });
  const refetch = () => { qc.invalidateQueries({ queryKey: ['curator', 'cue-set', set.id, 'words'] }); onChanged(); };

  const addM = useMutation({
    mutationFn: () => cueCardsApi.addWord(set.id, { word: w.word.trim(), translation: w.translation.trim() || undefined, example: w.example.trim() || undefined, level: set.level }),
    onSuccess: () => { setW({ word: '', translation: '', example: '' }); refetch(); },
  });

  const bulkM = useMutation({
    mutationFn: () => cueCardsApi.addWordsBulk(set.id, bulk),
    onSuccess: (res) => {
      setBulk('');
      // ⚠️ Показываем, СКОЛЬКИМ ученикам ушло. Без этого куратор не знает,
      // сработало ли вообще: раньше слова добавлялись «в никуда», и именно на
      // это жаловался заказчик.
      setBulkNote(
        t('cueSets.bulkDone', { added: res.added.length, students: res.students }) +
          (res.skipped.length ? ' · ' + t('cueSets.bulkSkipped', { n: res.skipped.length }) : ''),
      );
      refetch();
    },
    onError: () => setBulkNote(t('cueSets.bulkFailed')),
  });
  const removeM = useMutation({
    mutationFn: (wordId: string) => cueCardsApi.removeWord(set.id, wordId),
    onSuccess: refetch,
  });
  const regenM = useMutation({
    mutationFn: (wordId: string) => cueCardsApi.regenerateWord(set.id, wordId),
    onSuccess: refetch,
  });

  // Содержимое набора — одно на обе раскладки: на компьютере раскрывается в карточке, на телефоне — экран набора.
  const body = (
        <div className={cn('space-y-3', !phone && 'mt-3 border-t border-outline-variant/60 pt-3')}>
          {/* ⚠️ СПИСОК СЛОВ — ОСНОВНОЙ СПОСОБ, и он стоит ПЕРВЫМ.
              Заказчик 05.09.2026: «просто слова через запятую пишешь — и они
              добавляются ученикам в разделе vocabulary». Ввод по одному слову
              оставлен ниже: он нужен, когда надо задать свой перевод и пример. */}
          <div className="rounded-card bg-surface-container-low p-3">
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-label text-on-surface-variant">
              {t('cueSets.bulkLabel')}
            </label>
            <textarea
              rows={3}
              value={bulk}
              onChange={(e) => setBulk(e.target.value)}
              placeholder={t('cueSets.bulkPlaceholder')}
              className="w-full rounded-xl border border-outline-variant bg-surface px-3 py-2 text-sm text-on-surface"
            />
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <Button loading={bulkM.isPending} disabled={!bulk.trim()} onClick={() => bulkM.mutate()}>
                <span className="material-symbols-outlined">playlist_add</span>
                {t('cueSets.bulkAdd')}
              </Button>
              {bulkNote && <span className="text-xs text-on-surface-variant">{bulkNote}</span>}
            </div>
            <p className="mt-1.5 text-xs text-on-surface-variant">{t('cueSets.bulkHint')}</p>
          </div>

          {/* add word */}
          <div className="grid gap-2 sm:grid-cols-[1fr_1fr_1.4fr_auto] sm:items-end">
            <Input label={t('cueSets.word')} value={w.word} onChange={(e) => setW({ ...w, word: e.target.value })} placeholder="resilient" />
            <Input label={t('cueSets.translation')} value={w.translation} onChange={(e) => setW({ ...w, translation: e.target.value })} placeholder={t('cueSets.translationExample')} />
            <Input label={t('cueSets.example')} value={w.example} onChange={(e) => setW({ ...w, example: e.target.value })} placeholder="She stayed resilient…" />
            <Button loading={addM.isPending} disabled={!w.word.trim()} onClick={() => addM.mutate()}>
              <span className="material-symbols-outlined">add</span>
            </Button>
          </div>
          <p className="text-xs text-on-surface-variant">
            {t('cueSets.autoNote')}
          </p>

          {/* word list */}
          {words.isLoading ? (
            <p className="text-sm text-on-surface-variant">{t('cueSets.loadingWords')}</p>
          ) : (words.data?.items ?? []).length === 0 ? (
            <p className="text-sm text-on-surface-variant">{t('cueSets.noWords')}</p>
          ) : (
            <ul className="space-y-2">
              {(words.data?.items ?? []).map((word) => (
                <CuratorWordRow
                  key={word.id}
                  word={word}
                  regenerating={regenM.isPending && regenM.variables === word.id}
                  onRegenerate={() => regenM.mutate(word.id)}
                  onRemove={() => removeM.mutate(word.id)}
                  onMore={phone ? () => setWordSheet(word) : undefined}
                />
              ))}
            </ul>
          )}
        </div>
  );

  if (phone) {
    const meta = [t('cueSets.wordCount', { n: set.word_count }), set.level, marathonTitle ?? t('cueSets.noMarathon')]
      .filter(Boolean)
      .join(' · ');
    return (
      <div className="space-y-3">
        <div className="flex items-start gap-2.5 pb-1 pt-1.5">
          <button
            type="button"
            onClick={onToggle}
            aria-label={t('cueSets.title')}
            className="grid h-11 w-11 flex-none place-items-center rounded-full bg-surface-container-low text-on-surface"
          >
            <span className="material-symbols-outlined text-[18px]">chevron_left</span>
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-[22px] font-bold leading-tight text-on-surface">{set.title}</h1>
            <p className="mt-0.5 text-[11.5px] text-on-surface-variant">{meta}</p>
          </div>
          <button
            type="button"
            onClick={onDelete}
            aria-label={t('cueSets.deleteSet')}
            className="grid h-11 w-11 flex-none place-items-center rounded-full text-on-surface-variant"
          >
            <span className="material-symbols-outlined text-[20px]">delete</span>
          </button>
        </div>
        {body}
        <ActionSheet
          open={!!wordSheet}
          onClose={() => setWordSheet(null)}
          title={wordSheet?.word}
          actions={
            wordSheet
              ? [
                  { label: t('cueSets.regenerate'), icon: 'autorenew', onSelect: () => regenM.mutate(wordSheet.id) },
                  { label: t('cueSets.removeWord'), icon: 'close', danger: true, onSelect: () => removeM.mutate(wordSheet.id) },
                ]
              : []
          }
        />
      </div>
    );
  }

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <CardTitle>{set.title}</CardTitle>
          <div className="mt-1 flex flex-wrap gap-2">
            <Pill variant="primary">{t('cueSets.wordCount', { n: set.word_count })}</Pill>
            {set.level && <Pill variant="neutral">{set.level}</Pill>}
            {marathonTitle ? <Pill variant="success">{marathonTitle}</Pill> : <Pill variant="warning">{t('cueSets.noMarathon')}</Pill>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={onToggle}>
            <span className="material-symbols-outlined">{open ? 'expand_less' : 'edit'}</span>
            {t(open ? 'cueSets.collapse' : 'cueSets.words')}
          </Button>
          <button onClick={onDelete} title={t('cueSets.deleteSet')} className="rounded-button p-1.5 text-on-surface-variant hover:text-error">
            <span className="material-symbols-outlined text-base">delete</span>
          </button>
        </div>
      </div>

      {open && body}
    </Card>
  );
}

// One word in the curator's set — shows exactly what the student will get: every
// AI-generated meaning (numbered POS · RU/KK) + inflected forms, with a
// regenerate action for when the autofill was empty or wrong.
function CuratorWordRow({
  word, regenerating, onRegenerate, onRemove, onMore,
}: {
  word: CueWord; regenerating: boolean; onRegenerate: () => void; onRemove: () => void;
  /** Телефон: одна кнопка «⋯» 44 px вместо двух значков 28 px — действия уходят в лист (§4.15). */
  onMore?: () => void;
}) {
  const { t } = useTranslation();
  const senses = word.senses ?? [];
  const chips = formChips(word.forms);
  const pending = senses.length === 0;
  return (
    <li className="rounded-card bg-surface-container-lowest p-3 ghost-border">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="text-[15px] font-bold text-on-surface">{word.word}</span>
          {word.audio_url && (
            <span className="material-symbols-outlined ml-1 align-middle text-sm text-primary">volume_up</span>
          )}
          {senses.length > 1 && (
            <span className="ml-2 rounded-pill bg-primary-fixed px-2 py-0.5 text-[10px] font-bold uppercase tracking-label text-primary">
              {t('cueSets.sensesShort', { count: senses.length })}
            </span>
          )}
        </div>
        {onMore ? (
          <button
            type="button"
            onClick={onMore}
            aria-label={t('nav.more')}
            className="-my-2 -mr-2 grid h-11 w-11 flex-none place-items-center rounded-full text-on-surface-variant"
          >
            <span className={cn('material-symbols-outlined text-[20px]', regenerating && 'animate-spin')}>
              {regenerating ? 'progress_activity' : 'more_horiz'}
            </span>
          </button>
        ) : (
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            onClick={onRegenerate}
            disabled={regenerating}
            title={t('cueSets.regenerate')}
            className="rounded-button p-1.5 text-on-surface-variant hover:text-primary disabled:opacity-50"
          >
            <span className={cn('material-symbols-outlined text-base', regenerating && 'animate-spin')}>
              {regenerating ? 'progress_activity' : 'autorenew'}
            </span>
          </button>
          <button onClick={onRemove} title={t('cueSets.removeWord')} className="rounded-button p-1.5 text-on-surface-variant hover:text-error">
            <span className="material-symbols-outlined text-base">close</span>
          </button>
        </div>
        )}
      </div>

      {pending ? (
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-on-surface-variant">
          <span className="material-symbols-outlined animate-spin text-sm">progress_activity</span>
          {t(regenerating ? 'cueSets.generating' : 'cueSets.generatingBg')}
          {word.translation && <span className="text-on-surface-variant/70">{t('cueSets.forNow', { translation: word.translation })}</span>}
        </p>
      ) : (
        <ol className="mt-2 space-y-1.5">
          {senses.map((s, i) => {
            const ru = s.translation_ru?.trim();
            const kk = s.translation_kk?.trim();
            const posK = posKey(s.pos);
            const pos = posK ? t(posK) : (s.pos ?? '');
            return (
              <li key={i} className="flex gap-2 text-sm">
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary-fixed text-[10px] font-bold text-primary">
                  {i + 1}
                </span>
                <span className="min-w-0">
                  {pos && <span className="mr-1 text-[10px] font-semibold uppercase tracking-label text-on-surface-variant">{pos}</span>}
                  {s.ipa?.trim() && <span className="mr-1 font-mono text-xs text-on-surface-variant/70">{s.ipa.trim()}</span>}
                  <span className="font-medium text-on-surface">{ru || kk || '—'}</span>
                  {ru && kk && <span className="text-xs text-on-surface-variant/70"> · {kk}</span>}
                  {s.example?.trim() && (
                    <span className="block text-xs italic text-on-surface-variant/80">“{s.example.trim()}”</span>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {chips.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {chips.map((c) => (
            <span key={c.labelKey} className="inline-flex items-baseline gap-1 rounded-pill bg-surface-container-low px-2 py-0.5 text-xs text-on-surface">
              <span className="text-[10px] uppercase tracking-label text-on-surface-variant/70">{t(c.labelKey)}</span>
              <span className="font-semibold">{c.value}</span>
            </span>
          ))}
        </div>
      )}
    </li>
  );
}
